import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import ReactECharts from "@/components/EChartsReact";
import { CONTRACT_STATUS_LABELS } from "./constants";
import {
  ChartCanvas,
  ChartEmpty,
  ChartHeader,
  ChartPanel,
  ChartsGrid,
  ChartTitle,
} from "./ContractCharts.styles";

const STATUS_COLORS = {
  submitted: "#d99a17",
  in_progress: "#2878c8",
  completed: "#2f9b67",
  closed: "#8a94a3",
};

const PRIORITY_COLORS = {
  Urgent: "#d64545",
  Important: "#d49319",
  Low: "#6f8092",
};

const countBy = (rows, getKey, getLabel, getColor) => {
  const counts = new Map();

  rows.forEach((row, index) => {
    const rawKey = getKey(row);
    const label = getLabel(row);
    if (rawKey === null || rawKey === undefined || !label) return;

    const key = String(rawKey);
    const current = counts.get(key);
    if (current) {
      current.value += 1;
      return;
    }

    counts.set(key, {
      key: rawKey,
      name: label,
      value: 1,
      color: getColor(row, index),
    });
  });

  return Array.from(counts.values()).sort(
    (left, right) => right.value - left.value || left.name.localeCompare(right.name)
  );
};

const withSelectionStyle = (data, selectedKeys, treemap = false) => {
  const selected = new Set((selectedKeys || []).map(String));
  const hasSelection = selected.size > 0;

  return data.map((item) => ({
    ...item,
    itemStyle: {
      color: item.color,
      opacity: hasSelection && !selected.has(String(item.key)) ? 0.28 : 1,
      ...(treemap ? { borderColor: "#ffffff", borderWidth: 3, gapWidth: 3 } : {}),
    },
  }));
};

const buildDonutOption = (data, selectedKeys) => {
  const chartData = withSelectionStyle(data, selectedKeys);
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return {
    animationDuration: 250,
    title: {
      text: String(total),
      subtext: "Total",
      left: "center",
      top: "38%",
      textStyle: { color: "#1f2937", fontSize: 24, fontWeight: 700 },
      subtextStyle: { color: "#7b8492", fontSize: 11 },
    },
    tooltip: {
      trigger: "item",
      formatter: "{b}: {c} ({d}%)",
    },
    legend: { show: false },
    series: [{
      type: "pie",
      cursor: "pointer",
      radius: ["46%", "70%"],
      center: ["50%", "50%"],
      minAngle: 3,
      label: { show: false },
      emphasis: { scaleSize: 5 },
      data: chartData,
    }],
  };
};

const buildTreemapOption = (data, selectedKeys) => ({
  animationDuration: 250,
  tooltip: {
    formatter: (params) => `${params.name}: ${params.value}`,
  },
  series: [{
    type: "treemap",
    cursor: "pointer",
    roam: false,
    nodeClick: false,
    breadcrumb: { show: false },
    top: 4,
    right: 4,
    bottom: 4,
    left: 4,
    label: {
      show: true,
      formatter: "{b}\n{c}",
      color: "#ffffff",
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 16,
      overflow: "truncate",
    },
    upperLabel: { show: false },
    levels: [{ itemStyle: { borderColor: "#ffffff", borderWidth: 3, gapWidth: 3 } }],
    data: withSelectionStyle(data, selectedKeys, true),
  }],
});

function Chart({ title, data, option, onSelect }) {
  const onEvents = useMemo(() => ({
    click: (params) => {
      if (params?.data?.key !== undefined) onSelect?.(params.data.key);
    },
  }), [onSelect]);

  return (
    <ChartPanel>
      <ChartHeader>
        <ChartTitle>{title}</ChartTitle>
      </ChartHeader>
      <ChartCanvas>
        {data.length ? (
          <ReactECharts
            style={{ width: "100%", height: "100%" }}
            option={option}
            onEvents={onEvents}
            notMerge
            lazyUpdate
          />
        ) : (
          <ChartEmpty>No data</ChartEmpty>
        )}
      </ChartCanvas>
    </ChartPanel>
  );
}

export default function ContractCharts({
  rows,
  filters,
  onPrioritySelect,
  onStatusSelect,
}) {
  const isPC = useSelector((state) => state.App?.isPC ?? true);

  const priorityData = useMemo(() => countBy(
    rows,
    (row) => row.priority,
    (row) => row.priority,
    (row) => PRIORITY_COLORS[row.priority] || "#8a94a3"
  ), [rows]);

  const statusData = useMemo(() => countBy(
    rows,
    (row) => row.status,
    (row) => CONTRACT_STATUS_LABELS[row.status] || row.status,
    (row) => STATUS_COLORS[row.status] || "#8a94a3"
  ), [rows]);

  if (!isPC) return null;

  return (
    <ChartsGrid aria-label="Contract request distributions">
      <Chart
        title="Priority"
        data={priorityData}
        option={buildDonutOption(priorityData, filters.priority)}
        onSelect={onPrioritySelect}
      />
      <Chart
        title="Status"
        data={statusData}
        option={buildTreemapOption(statusData, filters.status)}
        onSelect={onStatusSelect}
      />
    </ChartsGrid>
  );
}
